import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useAuth } from "../contexts/AuthContext";
import { trpcReact } from "../trpc_client";
import AuthModal from "./AuthModal";

type JoinStatus = "idle" | "joining" | "success" | "error" | "already_in_flat";

const JoinFlat = () => {
  const { token, flatId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const colors = useColors();
  const { user, loading: authLoading } = useAuth();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [joinStatus, setJoinStatus] = useState<JoinStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const joinFlatMutation = trpcReact.addUserToFlat.useMutation();

  const handleJoinFlat = async () => {
    if (!token) {
      setJoinStatus("error");
      setErrorMessage(t("invalid_join_code") || "Invalid join code");
      return;
    }

    setJoinStatus("joining");
    setErrorMessage("");

    try {
      await joinFlatMutation.mutateAsync({ join_code: token });
      setJoinStatus("success");
    } catch (error: any) {
      if (error.message?.includes("already in flat")) {
        setJoinStatus("already_in_flat");
      } else if (error.message?.includes("not found")) {
        setJoinStatus("error");
        setErrorMessage(
          t("join_code_not_found") || "Join code not found or expired",
        );
      } else {
        setJoinStatus("error");
        setErrorMessage(
          error.message || t("join_flat_error") || "Failed to join flat",
        );
      }
    }
  };

  // Attempt to join when user is authenticated
  useEffect(() => {
    if (!authLoading && user && joinStatus === "idle") {
      handleJoinFlat();
    }
  }, [user, authLoading, joinStatus]);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // The useEffect will trigger join after auth
  };

  const handleGoToFlats = () => {
    navigate("/flats", { replace: true });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="text-lg" style={{ color: colors.textNeutralMuted }}>
          {t("loading") || "Loading..."}
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="max-w-md w-full text-center space-y-6">
          {/* Icon */}
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.containerNeutralLightLowest }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: colors.accentColor }}
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
          </div>

          {/* Title & Description */}
          <div className="space-y-2">
            <h1
              className="text-3xl font-semibold"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("join_flat_title") || "Join a Flat"}
            </h1>
            <p className="text-lg" style={{ color: colors.textNeutralMuted }}>
              {t("join_flat_login_prompt") ||
                "Sign in to join this flat and connect with your flatmates."}
            </p>
          </div>

          {/* Sign in button */}
          <button
            onClick={() => setShowAuthModal(true)}
            className="w-full rounded-full px-8 py-4 text-lg font-semibold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {t("sign_in") || "Sign in"}
          </button>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </div>
    );
  }

  // Joining in progress
  if (joinStatus === "joining") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="max-w-md w-full text-center space-y-6">
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center animate-pulse"
            style={{ backgroundColor: colors.accentColor }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
          </div>
          <p
            className="text-xl font-medium"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("joining_flat") || "Joining flat..."}
          </p>
        </div>
      </div>
    );
  }

  // Success
  if (joinStatus === "success") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="max-w-md w-full text-center space-y-6">
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.accentColor }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>

          <div className="space-y-2">
            <h1
              className="text-3xl font-semibold"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("join_flat_success_title") || "Welcome!"}
            </h1>
            <p className="text-lg" style={{ color: colors.textNeutralMuted }}>
              {t("join_flat_success_message") ||
                "You have successfully joined the flat."}
            </p>
          </div>

          <button
            onClick={handleGoToFlats}
            className="w-full rounded-full px-8 py-4 text-lg font-semibold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {t("go_to_flats") || "Go to My Flats"}
          </button>
        </div>
      </div>
    );
  }

  // Already in flat
  if (joinStatus === "already_in_flat") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="max-w-md w-full text-center space-y-6">
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.containerNeutralLightLowest }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: colors.accentColor }}
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>

          <div className="space-y-2">
            <h1
              className="text-3xl font-semibold"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("already_in_flat_title") || "Already a Member"}
            </h1>
            <p className="text-lg" style={{ color: colors.textNeutralMuted }}>
              {t("already_in_flat_message") ||
                "You are already a member of this flat."}
            </p>
          </div>

          <button
            onClick={handleGoToFlats}
            className="w-full rounded-full px-8 py-4 text-lg font-semibold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {t("go_to_flats") || "Go to My Flats"}
          </button>
        </div>
      </div>
    );
  }

  // Error
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      <div className="max-w-md w-full text-center space-y-6">
        <div
          className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
          style={{ backgroundColor: "#fee2e2" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </div>

        <div className="space-y-2">
          <h1
            className="text-3xl font-semibold"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("join_flat_error_title") || "Could not join flat"}
          </h1>
          <p className="text-lg" style={{ color: colors.textNeutralMuted }}>
            {errorMessage ||
              t("join_flat_error_message") ||
              "Something went wrong. Please try again."}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              setJoinStatus("idle");
              setErrorMessage("");
            }}
            className="w-full rounded-full px-8 py-4 text-lg font-semibold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {t("try_again") || "Try Again"}
          </button>
          <button
            onClick={handleGoToFlats}
            className="w-full rounded-full px-8 py-4 text-lg font-medium transition-all hover:opacity-80"
            style={{
              backgroundColor: colors.containerNeutralLightMedium,
              color: colors.textNeutralDefault,
            }}
          >
            {t("go_to_flats") || "Go to My Flats"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinFlat;
